<template>
  <div class="min-h-screen w-full min-w-0 bg-gray-50 text-gray-900 dark:bg-gray-950 dark:text-gray-100" style="font-family: 'Inter', sans-serif;">
    <div class="predix-casting-layout flex min-h-screen w-full min-w-0">
      <!-- Left composition panel (~1/3 of row; explicit flex-basis so width is reliable) -->
      <aside
          data-tour="predix-composition-aside"
          class="predix-composition-aside sticky top-0 z-50 h-screen flex flex-col box-border border-r border-gray-200/90 bg-gradient-to-b from-gray-100 via-gray-50 to-white dark:border-white/10 dark:from-gray-900 dark:via-gray-950 dark:to-gray-950"
          :class="compositionPanelOpen ? 'predix-composition-aside--open overflow-hidden' : 'predix-composition-aside--collapsed overflow-y-auto no-scrollbar'"
      >
        <!-- v-show (not v-if) keeps nodes during width transition so collapse doesn’t snap -->

        <div
            class="mt-5 mb-1 shrink-0 box-border px-4 pt-0 sm:mb-0 sm:px-5 md:px-6"
            v-show="compositionPanelOpen"
        >
          <h1
            data-tour="predix-intro"
            class="text-2xl md:text-3xl text-gray-800 dark:text-gray-100 font-bold leading-tight"
          >
            PrediX™
          </h1>
        </div>


        <div
            v-show="compositionPanelOpen"
            class="relative flex-1 min-h-0 min-w-0 w-full"
        >

          <div
              class="absolute inset-0 box-border overflow-y-auto no-scrollbar px-4 pt-0 pb-16 sm:px-5 sm:pb-16 md:px-6 md:pb-16"
          >
            <div class="flex flex-col items-center box-border w-full min-w-0">
              <!-- ~1/3 viewport tall zone for DB pills only (avoids empty half-screen) -->

              <div
                  class="w-full min-w-0 flex flex-col gap-2 items-center text-center pt-5 pb-0"
                  :class="!isDatabaseSelected ? 'min-h-0 max-h-[40vh] justify-start pt-0' : 'mb-0'"
              >
                <div class="flex w-full min-w-0 items-center gap-2 px-1">
                <ul class="flex flex-1 min-w-0 flex-row flex-nowrap items-center justify-center gap-3">
                  <li class="flex-1 min-w-0">
                    <button
                        type="button"
                        data-tour="predix-main-db"
                        class="predix-dataset-pill-trigger w-full justify-center text-center"
                        :class="{ 'predix-dataset-pill-trigger--current': selectedDatabaseTab === 0 }"
                        :aria-current="selectedDatabaseTab === 0 ? 'true' : undefined"
                        @click.stop="onDatabasePillClick('normal')"
                    >
                      <CTailwindPill
                        tag="span"
                        class="predix-dataset-pill w-full justify-center text-center"
                        :active="selectedDatabaseTab === 0"
                      >
                        Main DB
                      </CTailwindPill>
                    </button>
                  </li>
                  <li class="flex-1 min-w-0">
                    <button
                        type="button"
                        data-tour="predix-benchmark-db"
                        class="predix-dataset-pill-trigger w-full justify-center text-center"
                        :class="{ 'predix-dataset-pill-trigger--current': selectedDatabaseTab === 1 }"
                        :aria-current="selectedDatabaseTab === 1 ? 'true' : undefined"
                        @click.stop="onDatabasePillClick('standard')"
                    >
                      <CTailwindPill
                        tag="span"
                        class="predix-dataset-pill w-full justify-center text-center"
                        :active="selectedDatabaseTab === 1"
                      >
                        Standard DB
                      </CTailwindPill>
                    </button>
                  </li>
                </ul>
                <button
                    v-if="isDatabaseSelected"
                    type="button"
                    class="predix-dataset-refresh shrink-0"
                    aria-label="Change databases"
                    title="Change databases"
                    @click.stop="resetDatabaseSelection"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="h-4 w-4" aria-hidden="true">
                    <path fill-rule="evenodd" d="M15.312 11.424a5.5 5.5 0 0 1-9.201 2.466l-.312-.311h2.433a.75.75 0 0 0 0-1.5H3.989a.75.75 0 0 0-.75.75v4.242a.75.75 0 0 0 1.5 0v-2.43l.31.31a7 7 0 0 0 11.712-3.138.75.75 0 0 0-1.449-.39Zm1.23-3.723a.75.75 0 0 0 .219-.53V2.929a.75.75 0 0 0-1.5 0V5.36l-.31-.31A7 7 0 0 0 3.239 8.188a.75.75 0 1 0 1.448.389A5.5 5.5 0 0 1 13.89 6.11l.311.31h-2.432a.75.75 0 0 0 0 1.5h4.243a.75.75 0 0 0 .53-.219Z" clip-rule="evenodd" />
                  </svg>
                </button>
                </div>
                <p
                    v-if="activeDatabaseDisplayName"
                    class="w-full px-1 mt-1 ml-2  text-xs text-left font-medium text-gray-500/60 dark:text-gray-400/60 truncate"
                >
                  {{ activeDatabaseDisplayName }}
                </p>
              </div>

              <div class="w-full min-w-0">
                <AlloyPropertySelector
                    v-if="isDatabaseSelected"
                    variant="sidepanel"
                    :package_id="PACKAGE_ID"
                    :standardAlloys="standardAlloys"
                    :standardDatabaseId="selectedStandardDatabaseId"
                    :databaseId="selectedDatabaseTab == 0 ? selectedNormalDatabaseId : selectedStandardDatabaseId"
                    :databaseType="selectedDatabaseTab == 0 ? 1 : 2"
                    :fieldGroups="fieldGroups"
                    v-on:onExplore="onExplore"
                    v-on:onExploreStart="onExploreStart"
                    @composition-type-change="onCompositionTypeChange"
                />
                <template v-else>
                  <div
                      data-tour="predix-main-inputs"
                      class="mt-4 rounded-xl border border-dashed border-gray-300/90 bg-white/60 px-4 py-5 text-center text-sm text-gray-500 dark:border-white/15 dark:bg-white/5 dark:text-gray-400"
                  >
                    Select Main DB to enter element percentages. You can start a prediction from Main DB alone.
                  </div>
                  <div
                      data-tour="predix-benchmark-input"
                      class="mt-3 rounded-xl border border-dashed border-gray-300/90 bg-white/60 px-4 py-4 text-center text-sm text-gray-500 dark:border-white/15 dark:bg-white/5 dark:text-gray-400"
                  >
                    With Standard DB, pick a Standard alloy to compare against.
                  </div>
                  <div
                      data-tour="predix-start-prediction"
                      class="mt-3 flex min-h-[3rem] w-full items-center justify-center rounded-xl border border-gray-200 bg-gray-100/80 px-4 py-3 text-[0.9375rem] font-semibold tracking-wide text-gray-400 dark:border-white/10 dark:bg-white/5 dark:text-gray-500"
                      aria-hidden="true"
                  >
                    START PREDICTION
                  </div>
                </template>
              </div>
            </div>
          </div>

          <div
              class="pointer-events-none absolute inset-x-0 bottom-0 z-10 flex justify-center border-t border-gray-200/90 bg-white/95 py-1 px-3 dark:border-white/10 dark:bg-gray-950"
          >
            <button
                type="button"
                class="pointer-events-auto h-9 w-9 rounded-full flex items-center justify-center border border-gray-300/90 bg-gray-100 hover:bg-gray-200 text-gray-800 dark:border-white/10 dark:bg-white/5 dark:hover:bg-white/10 dark:text-gray-100"
                @click="compositionPanelOpen = false"
                aria-label="Collapse composition panel"
            >
              <span class="text-xl leading-none">←</span>
            </button>
          </div>
        </div>

        <div
            v-show="!compositionPanelOpen"
            class="flex flex-1 min-h-0 flex-col items-center justify-end box-border px-2 pb-2 pt-2"
        >
          <button
              type="button"
              class="h-10 w-10 rounded-full shrink-0 flex items-center justify-center border border-gray-300/90 bg-gray-100 hover:bg-gray-200 text-gray-800 dark:border-white/10 dark:bg-white/5 dark:hover:bg-white/10 dark:text-gray-100"
              @click="compositionPanelOpen = true"
              aria-label="Expand composition panel"
          >
            <span class="text-xl leading-none">→</span>
          </button>
        </div>
      </aside>

      <!-- Right content: fills remaining space (~2/3) after composition panel (~1/3) -->
      <main class="predix-casting-main flex-1 min-w-0 bg-gray-50 px-4 pb-6 dark:bg-gray-950 sm:px-6">
        <div class="flex items-center justify-between mb-6">
          <div>
            <div class="text-2xl font-semibold text-gray-900 dark:text-gray-100">PrediX Platform</div>
            <div class="text-sm text-gray-600 dark:text-gray-400 mt-1">Explore properties and compare alloys.</div>
          </div>
        </div>

        <div class="flex flex-col gap-4">
          <PredixResultTabs
            :value="resultTabActive"
            :interactive="resultTabsInteractive"
            :can-report="canCreateReport"
            :can-favorite="canSaveFavorite"
            :show-favorite="showFavoriteAction"
            @input="resultTabActive = $event"
            @report="openReportDialog"
            @favorite-save="openFavoriteSaveDialog"
          />

          <template v-if="!resultTabsInteractive && !(isPredicting && isDatabaseSelected)">
            <PredixCastingResultsPlaceholder />
          </template>

          <div
            v-else-if="isPredicting && !isExplored"
            class="flex min-h-[min(360px,48vh)] flex-col items-center justify-center rounded-xl border border-gray-200 bg-white px-8 py-20 shadow-sm dark:border-white/10 dark:bg-white/5 dark:shadow-none"
          >
              <div class="flex flex-col items-center justify-center gap-6 text-gray-700 dark:text-gray-300">
                <div
                  class="h-14 w-14 shrink-0 rounded-full border-4 border-gray-300 border-t-gray-700 animate-spin dark:border-white/20 dark:border-t-white"
                ></div>
                <div class="text-base font-medium">Running prediction…</div>
            </div>
          </div>

          <template v-else-if="exploreResult.length > 0">
            <div v-if="resultTabActive == 0">
              <v-expansion-panels
                v-model="explorePanelOpen"
                class="predix-explore-panels pb-1"
                flat
              >
                <v-expansion-panel
                  class="mt-3"
                  style="background-color: transparent !important;"
                  v-for="(castingPropertyGroup, panelIndex) in exploreResultCastingPropertiesGroups"
                  :key="castingPropertyGroup.id"
                >
                  <v-expansion-panel-header
                    class="text-subtitle-2 expansionPanelHeader !h-16"
                    :data-tour="panelIndex === 0 ? 'predix-explore-accordion' : undefined"
                  >
                    <div class="flex items-center gap-3 w-full min-w-0">
                      <div
                        class="inline-flex !w-10 !h-10 !min-w-[40px] !max-w-[40px] !rounded-full items-center justify-center font-semibold text-sm shrink-0 overflow-hidden bg-gray-200 text-gray-800 dark:bg-gray-900 dark:text-white"
                      >
                        {{ castingPropertyGroup.name.trim().charAt(0).toUpperCase() }}
                      </div>
                      <div class="flex items-center min-w-0">
                        <span class="truncate">{{ castingPropertyGroup.name }}</span>
                      </div>
                    </div>
                  </v-expansion-panel-header>
                  <v-expansion-panel-content style="background-color: transparent !important;">
                    <CastingExploreResult
                      :ref="panelIndex === 0 ? 'firstExploreResult' : undefined"
                      :exploreResult="grouppedExploreResult[castingPropertyGroup.id]"
                      :castingPropertiesGroupId="castingPropertyGroup.id"
                      :castingPropertiesGroupName="castingPropertyGroup.name"
                      :standardAlloys="standardAlloys"
                      :favoriteAlloys="favoriteAlloys"
                      :standardDatasetName="standardDatasets.find((item) => item.id === selectedStandardDatabaseId).name"
                      :standardDatasetId="selectedStandardDatabaseId"
                      :package_id="PACKAGE_ID"
                      :databaseType="exploreDbType || (selectedDatabaseTab === 0 ? 1 : 2)"
                    ></CastingExploreResult>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
            </div>
            <div v-else class="min-w-0 max-w-full overflow-visible">
              <CastingAlloyComparison
                ref="alloyComparison"
                :selectedNormalDatabaseId="selectedNormalDatabaseId"
                :selectedStandardDatabaseId="selectedStandardDatabaseId"
                :normalDatabasePropertyData="exploreResult"
                :standardAlloys="standardAlloys"
                :favoriteAlloys="favoriteAlloys"
                :normalDatasetFields="normalDatasetFields"
                :fieldGroups="fieldGroups"
                :selectedHeatTreatment="selectedHeatTreatment"
                :selectedCastingGroups="selectedCastingGroups"
                :package_id="PACKAGE_ID"
              />
            </div>
          </template>

          <div
            v-else
            class="p-4 rounded-xl border border-gray-200 bg-white text-sm text-gray-700 shadow-sm dark:border-white/10 dark:bg-white/5 dark:text-gray-300 dark:shadow-none"
          >
            No result has found! change the parameters!
          </div>
        </div>
      </main>
    </div>

    <!-- Dataset picker -->
    <ModalBasicTailwind
        id="predix-dataset-picker-modal"
        :modal-open="datasetPickerOpen"
        :title="datasetPickerTitle"
        :subtitle="datasetPickerSubtitle"
        @close-modal="closeDatasetPicker"
    >
      <div class="px-5 py-4">
        <ul
            v-if="datasetPickerItems.length"
            class="max-h-[min(360px,55vh)] overflow-y-auto space-y-2 predix-dataset-picker-list"
            role="listbox"
            :aria-label="datasetPickerTitle"
        >
          <li
              v-for="item in datasetPickerItems"
              :key="item.id"
              role="option"
              :aria-selected="isDatasetItemSelected(item)"
          >
            <button
                type="button"
                class="w-full flex items-center gap-3 text-left py-3 px-4 rounded-lg shadow-xs transition border text-sm"
                :class="isDatasetItemSelected(item)
                  ? 'predix-dataset-modal-item--selected border-2 bg-gray-50 dark:bg-gray-700/30 text-gray-800 dark:text-gray-100'
                  : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700/60 hover:border-gray-300 dark:hover:border-gray-600 text-gray-800 dark:text-gray-100'"
                @click.stop="selectDatasetItem(item)"
            >
              <icon
                  :id="datasetPickerIconId"
                  class="shrink-0 w-5 h-5 text-gray-400 dark:text-gray-500"
              />
              <span class="truncate flex-1 min-w-0 font-medium">{{ item.name }}</span>
              <span
                  v-if="isDatasetItemSelected(item)"
                  class="predix-dataset-modal-check shrink-0 text-xs font-semibold"
              >✓</span>
            </button>
          </li>
        </ul>
        <p
            v-else
            class="text-sm text-gray-500 dark:text-gray-400 px-2 py-8 text-center rounded-lg bg-gray-50 dark:bg-gray-900/20"
        >
          No datasets available.
        </p>
      </div>
      <div class="px-5 py-4 border-t border-gray-200 dark:border-gray-700/60">
        <div class="flex flex-wrap justify-end space-x-2">
          <button
              type="button"
              class="btn-sm border-gray-200 dark:border-gray-700/60 hover:border-gray-300 dark:hover:border-gray-600 text-gray-800 dark:text-gray-300"
              @click.stop="closeDatasetPicker"
          >
            Close
          </button>
        </div>
      </div>
    </ModalBasicTailwind>

    <PredixReportDialog
      :modal-open="reportDialogOpen"
      :categories="reportCategories"
      :database-name="reportDatabaseName"
      @close-modal="reportDialogOpen = false"
    />
    <PredixFavoriteAlloyDialog
      :modal-open="favoriteDialogOpen"
      :initial-tab="favoriteDialogTab"
      :can-save-current="canSaveFavorite"
      :existing-names="favoriteExistingNames"
      :saved-alloys="favoriteAlloys"
      :composition-items="reportCompositionItems"
      :property-items="favoritePropertyItems"
      :package-id="PACKAGE_ID"
      :database-id="selectedNormalDatabaseId"
      :database-name="selectedNormalDatabaseName"
      :heat-treatment-group-id="selectedHeatTreatment"
      :casting-group-id="selectedCastingGroups"
      @close-modal="favoriteDialogOpen = false"
      @saved="onFavoriteSaved"
      @deleted="onFavoriteDeleted"
    />
  </div>
</template>


<script>
import {getFieldGroups} from "@/api/settings";
import {getDatasets, getData} from "@/api/user/dataset";
import AlloyPropertySelector from "@/components/Predix/AlloyPropertySelector";
import CastingExploreResult from '@/components/Predix/CastingExploreResult'
import CastingAlloyComparison from '@/components/Predix/CastingAlloyComparison'
import PredixCastingResultsPlaceholder from '@/components/Predix/PredixCastingResultsPlaceholder.vue'
import PredixResultTabs from '@/components/Predix/PredixResultTabs.vue'
import CTailwindPill from "@/components/Vuetify/CTailwindPill.vue";
import ModalBasicTailwind from '@/components/modal/ModalBasicTailwind.vue'
import PredixReportDialog from '@/components/Predix/PredixReportDialog.vue'
import PredixFavoriteAlloyDialog from '@/components/Predix/PredixFavoriteAlloyDialog.vue'
import { listFavoriteAlloys, normalizeFavoriteList } from '@/api/favoriteAlloys'
import { itemBelongsToGroup, filterPositivePredictedResults } from '@/utils/predixExploreResult'
import { sortAlloyElementKeys } from '@/utils/alloyElementOrder'

export default {
  name: "PredixCasting",
  components: {

    CTailwindPill,
    AlloyPropertySelector,
    CastingExploreResult,
    CastingAlloyComparison,
    PredixCastingResultsPlaceholder,
    PredixResultTabs,
    ModalBasicTailwind,
    PredixReportDialog,
    PredixFavoriteAlloyDialog
  },
  data: () => ({
    compositionPanelOpen: true,
    datasetPickerOpen: false,
    datasetPickerTarget: 'normal',
    selectedDatabaseTab: 0,
    PACKAGE_ID: 9,
    CASTING_GROUP: "CASTING",
    normalDatasets: [],
    standardDatasets: [],
    selectedNormalDatabaseId: null,
    selectedStandardDatabaseId: null,
    isExplored: false,
    isPredicting: false,
    exploreResult: [],

    resultTabActive: 0,
    explorePanelOpen: undefined,
    fieldGroups: [],
    standardAlloys: [],
    selectedStandardAlloy: 0,
    compositionType: "COMPOSITION_WT",
    standardAlloyProperties: [],
    pendingAutoSelect: false,
    exploreDbType: 1,
    reportDialogOpen: false,
    reportCompositionItems: [],
    favoriteDialogOpen: false,
    favoriteDialogTab: 'save',
    favoriteAlloys: [],
  }),
  created() {
    this.loadFieldGroups();
    this.loadDatasets();
  },
  mounted() {
    this.$eventBus.$on('predix-tour-show-main', this.showMainDbForTour)
    this.$eventBus.$on('predix-tour-show-benchmark', this.showBenchmarkDbForTour)
    this.$eventBus.$on('predix-tour-click-start-prediction', this.clickStartPredictionForTour)
    this.$eventBus.$on('predix-tour-show-predictions-tab', this.showPredictionsTabForTour)
    this.$eventBus.$on('predix-tour-open-first-accordion', this.openFirstAccordionForTour)
    this.$eventBus.$on('predix-tour-expand-first-row', this.expandFirstExploreRowForTour)
    this.$eventBus.$on('predix-tour-show-comparison-tab', this.showComparisonTabForTour)
    this.$eventBus.$on('predix-tour-select-comparison-property', this.selectComparisonPropertyForTour)
    this.$eventBus.$on('predix-tour-select-comparison-benchmark', this.selectComparisonBenchmarkForTour)
  },
  beforeDestroy() {
    this.$eventBus.$off('predix-tour-show-main', this.showMainDbForTour)
    this.$eventBus.$off('predix-tour-show-benchmark', this.showBenchmarkDbForTour)
    this.$eventBus.$off('predix-tour-click-start-prediction', this.clickStartPredictionForTour)
    this.$eventBus.$off('predix-tour-show-predictions-tab', this.showPredictionsTabForTour)
    this.$eventBus.$off('predix-tour-open-first-accordion', this.openFirstAccordionForTour)
    this.$eventBus.$off('predix-tour-expand-first-row', this.expandFirstExploreRowForTour)
    this.$eventBus.$off('predix-tour-show-comparison-tab', this.showComparisonTabForTour)
    this.$eventBus.$off('predix-tour-select-comparison-property', this.selectComparisonPropertyForTour)
    this.$eventBus.$off('predix-tour-select-comparison-benchmark', this.selectComparisonBenchmarkForTour)
  },
  computed: {
    selectedNormalDatabaseName() {
      return this.normalDatasets.find((item) => item.id === this.selectedNormalDatabaseId)?.name || "";
    },
    selectedStandardDatabaseName() {
      return this.standardDatasets.find((item) => item.id === this.selectedStandardDatabaseId)?.name || "";
    },
    isDatabaseSelected() {
      return this.selectedNormalDatabaseId && this.selectedStandardDatabaseId;
    },
    resultTabsInteractive() {
      return (
        this.isDatabaseSelected &&
        this.isExplored &&
        !this.isPredicting &&
        this.exploreResult.length > 0
      );
    },
    datasetPickerTitle() {
      return this.datasetPickerTarget === 'normal'
        ? 'Choose the base database.'
        : 'Choose the standard database.';
    },
    datasetPickerSubtitle() {
      return '';
    },
    datasetPickerItems() {
      return this.datasetPickerTarget === 'normal' ? this.normalDatasets : this.standardDatasets;
    },
    datasetPickerIconId() {
      return this.datasetPickerTarget === 'normal' ? 'designspace-database' : 'standard-database';
    },
    isNormalDatabaseChosen() {
      return this.selectedNormalDatabaseId != null && this.selectedNormalDatabaseId !== '';
    },
    isStandardDatabaseChosen() {
      return this.selectedStandardDatabaseId != null && this.selectedStandardDatabaseId !== '';
    },
    activeDatabaseDisplayName() {
      if (this.selectedDatabaseTab === 0) {
        return this.selectedNormalDatabaseName;
      }
      return this.selectedStandardDatabaseName;
    },
    /** DB used for the current prediction (for export naming) */
    reportDatabaseName() {
      const type = this.exploreDbType || (this.selectedDatabaseTab === 0 ? 1 : 2);
      if (Number(type) === 2) {
        return this.selectedStandardDatabaseName || this.activeDatabaseDisplayName;
      }
      return this.selectedNormalDatabaseName || this.activeDatabaseDisplayName;
    },
    canCreateReport() {
      return (
        this.isExplored &&
        !this.isPredicting &&
        filterPositivePredictedResults(this.exploreResult).length > 0
      );
    },
    canSaveFavorite() {
      return this.canCreateReport && Number(this.exploreDbType) === 1;
    },
    showFavoriteAction() {
      return this.isExplored && Number(this.exploreDbType) === 1;
    },
    favoriteExistingNames() {
      return (this.favoriteAlloys || []).map((item) => item && item.name).filter(Boolean);
    },
    favoritePropertyItems() {
      return (this.exploreResult || [])
        .filter((item) => item && item.name)
        .map((item) => ({ name: item.name, value: item.value }));
    },
    reportCategories() {
      const categories = [];
      if ((this.reportCompositionItems || []).length) {
        categories.push({
          id: 'composition',
          name: 'Alloy Composition',
          includeZero: true,
          items: (this.reportCompositionItems || []).map((item) => ({
            ...item,
            cat: 'Alloy Composition',
          })),
        });
      }
      const groups = this.exploreResultCastingPropertiesGroups || [];
      const assigned = new Set();
      for (const group of groups) {
        const groupName = this.alloyCategoryName((group && group.name) || 'Other');
        const rawItems = this.grouppedExploreResult[group.id] || [];
        const items = rawItems.map((item) => {
          if (item && item.id != null) assigned.add(String(item.id));
          return {
            ...item,
            cat: groupName,
          };
        });
        if (!items.length) continue;
        categories.push({
          id: group.id,
          name: groupName,
          items,
        });
      }
      const leftovers = (this.exploreResult || []).filter((item) => {
        if (!item) return false;
        if (item.id == null) return true;
        return !assigned.has(String(item.id));
      });
      if (leftovers.length) {
        const byCat = {};
        for (const item of leftovers) {
          const catName = this.alloyCategoryName(
            this.resolveExploreItemCatName(item) || 'Predictions'
          );
          if (!byCat[catName]) byCat[catName] = [];
          byCat[catName].push({ ...item, cat: catName });
        }
        Object.keys(byCat).forEach((catName) => {
          const already = categories.some((c) => c.name === catName);
          if (already) {
            const target = categories.find((c) => c.name === catName);
            target.items = target.items.concat(byCat[catName]);
          } else {
            categories.push({
              id: `cat-${catName}`,
              name: catName,
              items: byCat[catName],
            });
          }
        });
      }
      return categories;
    },
  },
  watch: {
    selectedNormalDatabaseId() {
      this.loadFavoriteAlloys();
    },
  },
  methods: {
    alloyCategoryName(name) {
      const raw = String(name || '').trim() || 'Other';
      if (/^alloy\b/i.test(raw)) return raw;
      if (/^composition$/i.test(raw)) return 'Alloy Composition';
      return `Alloy ${raw}`;
    },
    resolveExploreItemCatName(item) {
      const groupIds = (item && item.groups) || [];
      if (!groupIds.length || !(this.fieldGroups || []).length) return '';
      for (const groupId of groupIds) {
        const group = this.fieldGroups.find(
          (g) =>
            String(g.id) === String(groupId) &&
            String(g.groupType || '').toUpperCase() === 'CASTING_PROPERTIES'
        );
        if (group && group.name) return group.name;
      }
      for (const groupId of groupIds) {
        const group = this.fieldGroups.find((g) => String(g.id) === String(groupId));
        if (group && group.name) return group.name;
      }
      return '';
    },
    openReportDialog() {
      if (!this.canCreateReport) {
        this.$root.notify.show({
          message: 'Run a prediction first, then export a report.',
          type: 'warning',
        });
        return;
      }
      this.reportDialogOpen = true;
    },
    openFavoriteDialog(tab = 'save') {
      if (!this.showFavoriteAction) {
        this.$root.notify.show({
          message: 'Saved alloys are available for Main DB predictions.',
          type: 'warning',
        });
        return;
      }
      this.favoriteDialogTab = tab === 'list' ? 'list' : 'save';
      this.favoriteDialogOpen = true;
    },
    openFavoriteSaveDialog() {
      if (!this.canSaveFavorite) {
        this.$root.notify.show({
          message: 'Run a Main DB prediction first to save this alloy.',
          type: 'warning',
        });
        return;
      }
      this.openFavoriteDialog('save');
    },
    loadFavoriteAlloys() {
      const databaseId = this.selectedNormalDatabaseId;
      if (!databaseId) {
        this.favoriteAlloys = [];
        return;
      }
      listFavoriteAlloys(
        { databaseId, page: 1, page_size: 100 },
        (res) => {
          this.favoriteAlloys = normalizeFavoriteList(res);
        },
        () => {
          this.favoriteAlloys = [];
        }
      );
    },
    onFavoriteSaved() {
      this.loadFavoriteAlloys();
      if (this.$root.notify && this.$root.notify.show) {
        this.$root.notify.show({
          message: 'Alloy saved. You can compare it from Alloy Comparison.',
          type: 'success',
        });
      }
    },
    onFavoriteDeleted() {
      this.loadFavoriteAlloys();
      if (this.$root.notify && this.$root.notify.show) {
        this.$root.notify.show({
          message: 'Saved alloy removed.',
          type: 'success',
        });
      }
    },
    onDatabasePillClick(target) {
      const tab = target === 'normal' ? 0 : 1;

      if (this.isDatabaseSelected) {
        if (this.selectedDatabaseTab !== tab) {
          this.selectedDatabaseTab = tab;
          this.onDatabaseTabSwitch();
        }
        return;
      }

      if (target === "standard" && !this.isNormalDatabaseChosen) {
        this.advanceDatabaseSelection();
        return;
      }

      if (target === "normal") {
        this.openDatasetPicker("normal");
        return;
      }

      this.advanceDatabaseSelection();
    },
    onDatabaseTabSwitch() {
      this.isExplored = false;
      this.isPredicting = false;
      this.exploreResult = [];
      this.reportCompositionItems = [];
      this.resultTabActive = 0;
      this.favoriteDialogOpen = false;
    },
    resetDatabaseSelection() {
      this.selectedNormalDatabaseId = null;
      this.selectedStandardDatabaseId = null;
      this.selectedDatabaseTab = 0;
      this.isExplored = false;
      this.isPredicting = false;
      this.exploreResult = [];
      this.reportCompositionItems = [];
      this.resultTabActive = 0;
      this.standardAlloys = [];
      this.datasetPickerTarget = "normal";
      this.datasetPickerOpen = false;
      this.$nextTick(() => this.advanceDatabaseSelection());
    },
    applyDatasetSelection(target, item) {
      if (!item) return;
      if (target === "normal") {
        this.selectedNormalDatabaseId = item.id;
        this.onNormalDatasetChange();
        return;
      }
      this.selectedStandardDatabaseId = item.id;
      this.onStandardDatasetChange();
    },
    advanceDatabaseSelection() {
      if (!this.isNormalDatabaseChosen) {
        if (this.normalDatasets.length === 1) {
          this.applyDatasetSelection("normal", this.normalDatasets[0]);
          return this.$nextTick(() => this.promptBenchmarkDatabaseSelection());
        }
        this.openDatasetPicker("normal");
        return;
      }

      if (!this.isStandardDatabaseChosen) {
        this.promptBenchmarkDatabaseSelection();
        return;
      }

      this.selectedDatabaseTab = 0;
    },
    promptBenchmarkDatabaseSelection() {
      this.selectedDatabaseTab = 1;

      if (this.standardDatasets.length === 1) {
        this.applyDatasetSelection("standard", this.standardDatasets[0]);
        this.closeDatasetPicker();
        this.selectedDatabaseTab = 0;
        return;
      }

      this.openDatasetPicker("standard");
    },
    openDatasetPicker(target) {
      this.datasetPickerTarget = target;
      this.selectedDatabaseTab = target === "normal" ? 0 : 1;
      this.datasetPickerOpen = true;
    },
    closeDatasetPicker() {
      this.datasetPickerOpen = false;
    },
    showMainDbForTour() {
      this.closeDatasetPicker();
      this.compositionPanelOpen = true;
      this.selectedDatabaseTab = 0;
    },
    showBenchmarkDbForTour() {
      this.closeDatasetPicker();
      this.compositionPanelOpen = true;
      this.selectedDatabaseTab = 1;
      this.$nextTick(() => {
        const startBtn = this.$el && this.$el.querySelector('[data-tour="predix-start-prediction"]');
        if (startBtn && typeof startBtn.scrollIntoView === 'function') {
          startBtn.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        }
      });
    },
    clickStartPredictionForTour() {
      this.compositionPanelOpen = true;
      this.$nextTick(() => {
        const btn = this.$el && this.$el.querySelector('[data-tour="predix-start-prediction"]');
        if (btn && !btn.disabled) btn.click();
      });
    },
    showPredictionsTabForTour() {
      this.resultTabActive = 0;
    },
    openFirstAccordionForTour() {
      this.resultTabActive = 0;
      this.explorePanelOpen = 0;
    },
    expandFirstExploreRowForTour() {
      this.resultTabActive = 0;
      if (this.explorePanelOpen !== 0) this.explorePanelOpen = 0;
      this.$nextTick(() => {
        const child = this.$refs.firstExploreResult;
        const cmp = Array.isArray(child) ? child[0] : child;
        if (cmp && typeof cmp.expandFirstRowForTour === 'function') cmp.expandFirstRowForTour();
      });
    },
    showComparisonTabForTour() {
      this.resultTabActive = 1;
    },
    selectComparisonPropertyForTour() {
      this.resultTabActive = 1;
      this.$nextTick(() => {
        const cmp = this.$refs.alloyComparison;
        if (cmp && typeof cmp.selectFirstPropertyForTour === 'function') cmp.selectFirstPropertyForTour();
      });
    },
    selectComparisonBenchmarkForTour() {
      this.resultTabActive = 1;
      this.$nextTick(() => {
        const cmp = this.$refs.alloyComparison;
        if (cmp && typeof cmp.selectFirstBenchmarkAlloyForTour === 'function') cmp.selectFirstBenchmarkAlloyForTour();
      });
    },
    onCompositionTypeChange(type) {
      this.compositionType = type;
    },
    isDatasetItemSelected(item) {
      if (!item) return false;
      if (this.datasetPickerTarget === 'normal') return this.selectedNormalDatabaseId === item.id;
      return this.selectedStandardDatabaseId === item.id;
    },
    selectDatasetItem(item) {
      if (!item) return;
      const target = this.datasetPickerTarget;
      this.applyDatasetSelection(target, item);

      if (target === "normal" && !this.selectedStandardDatabaseId) {
        this.$nextTick(() => this.promptBenchmarkDatabaseSelection());
        return;
      }

      this.closeDatasetPicker();
      if (this.isDatabaseSelected) {
        this.selectedDatabaseTab = 0;
      }
    },
    loadFieldGroups() {
      const _this = this;
      getFieldGroups(function (res) {
        _this.fieldGroups = res;
        // Datasets may have loaded first and deferred auto-selection until the
        // field groups were available (needed by loadAlloyNames). Run it now.
        if (_this.pendingAutoSelect && !_this.selectedNormalDatabaseId && !_this.selectedStandardDatabaseId) {
          _this.pendingAutoSelect = false;
          _this.$nextTick(() => _this.advanceDatabaseSelection());
        }
      });
    },
    onNormalDatasetChange() {
      this.isExplored = false;
      this.isPredicting = false;
    },
    onStandardDatasetChange() {
      this.isExplored = false;
      this.isPredicting = false;
      this.loadAlloyNames();
    },
    loadAlloyNames() {
      const _this = this;
      const idGroup = this.fieldGroups.find((item) => item.groupType === "ID");
      if (!idGroup) {
        return;
      }
      const alloyNameGroupId = idGroup.id;
      getData(this.PACKAGE_ID, this.selectedStandardDatabaseId, [], [alloyNameGroupId], function (res) {
        _this.standardAlloys = res.result.map((item) => {
          var name = Object.keys(item)[0];
          return {id: item[name], name: item[name]};
        });
        if (_this.standardAlloys.length > 0) {
          _this.selectedStandardAlloy = _this.standardAlloys[0].id;
          _this.loadStandardAlloyProps();
        }
      });
    },
    loadStandardAlloyProps() {
      const _this = this;
      const compositionGroup = this.fieldGroups.find((item) => item.groupType === this.compositionType);
      if (!compositionGroup) {
        return;
      }
      const alloyNameGroupId = compositionGroup.id;
      getData(this.PACKAGE_ID, this.selectedStandardDatabaseId, [{f0: this.selectedStandardAlloy}], [alloyNameGroupId], function (res) {
        const alloy = res.result[0];
        _this.standardAlloyProperties = [];
        for (var key of sortAlloyElementKeys(Object.keys(alloy))) {
          _this.standardAlloyProperties.push({id: key, value: alloy[key]});
        }
      });
    },
    loadDatasets() {
      const _this = this;
      getDatasets(this.PACKAGE_ID, function (res) {
        _this.normalDatasets = res.datasets.filter((item) => item.datasetType.id === 1);
        _this.standardDatasets = res.datasets.filter((item) => item.datasetType.id === 2);
        if (!_this.selectedNormalDatabaseId && !_this.selectedStandardDatabaseId) {
          // Auto-selection of a single Benchmark DB triggers loadAlloyNames, which
          // needs fieldGroups. If they haven't arrived yet, defer until they do
          // (otherwise the flow throws and the Benchmark tab hangs on loading).
          if (_this.fieldGroups.length) {
            _this.$nextTick(() => _this.advanceDatabaseSelection());
          } else {
            _this.pendingAutoSelect = true;
          }
        }
      });
    },
    onExploreStart() {
      this.isExplored = false;
      this.isPredicting = true;
      this.resultTabActive = 0;
      this.exploreResult = [];
      this.grouppedExploreResult = {};
      this.exploreResultCastingPropertiesGroups = [];
      this.reportCompositionItems = [];
      this.reportDialogOpen = false;
    },
    onExplore(exploreResult, databaseFields, selectedHeatTreatment, selectedCastingGroups, exploreDbType, compositionItems) {
      this.isPredicting = false;
      this.exploreResult = exploreResult;
      this.grouppedExploreResult = {};
      this.normalDatasetFields = databaseFields;
      this.reportCompositionItems = Array.isArray(compositionItems) ? compositionItems : [];

      let castingPropertiesGroups = new Set();
      this.normalDatasetFields.forEach((field) => field.groups.filter((grp) => grp.groupType === "CASTING_PROPERTIES").forEach((g) => castingPropertiesGroups.add(g.id)));
      for (const groupId of castingPropertiesGroups) {
        this.grouppedExploreResult[groupId] = exploreResult.filter((r) => itemBelongsToGroup(r, groupId));
      }
      this.exploreResultCastingPropertiesGroups = this.fieldGroups.filter((item) =>
        [...castingPropertiesGroups].some((groupId) => String(groupId) === String(item.id))
      );
      this.selectedCastingGroups = selectedCastingGroups;
      this.selectedHeatTreatment = selectedHeatTreatment;
      this.exploreDbType = exploreDbType;
      this.isExplored = true;
    },
  },
};
</script>

<style scoped>
.v-tab--active {
  color: black !important;
}

.theme--dark .v-tab--active {
  color: white !important;
}

.ellipse-bg {
  background-image: radial-gradient(farthest-corner at 100% 0%, rgba(3, 147, 168, 0.40) 0%, transparent 45%);
  background-repeat: no-repeat;
  background-attachment: fixed;
  background-position-x: right;
  background-position-y: center;
  background-size: 100vw 100vh;
  min-height: 100vh;
}

.ellipse-bg-bottom {
  background-image: radial-gradient(farthest-corner at 0% 100%, rgba(3, 147, 168, 0.40) 0%, transparent 45%);
  background-repeat: no-repeat;
  background-attachment: fixed;
  background-position-x: left;
  background-position-y: center;
  background-size: 100vw 100vh;
  min-height: 100vh;
}

.tabHeaderBox {
  box-shadow: 0px 7px 64px 10px #00000012;
  border-radius: 6px;
  background-color: white;
  padding: 12px;
  /* position: relative; */
  width: 100%;
}

.theme--dark .tabHeaderBox {
  background-color: #1e1e1e;

}

.expansionPanelHeader {
  background-color: white !important;
  border: none !important;
  border-radius: 6px !important;
}

.theme--dark .expansionPanelHeader {
  background-color: #1F2937 !important;
  border: none !important;
}

.expansionPanelHeader.v-expansion-panel-header--active {
  background-color: white !important;
  border: none !important;
  border-radius: 6px 6px 0 0 !important;
}

.theme--dark .expansionPanelHeader.v-expansion-panel-header--active {
  background-color: #1f2937 !important;
  border: none !important;
  border-radius: 6px 6px 0 0 !important;
}


.filterBox {
  border: 1px solid var(--v-primary-base);
  border-radius: 16px;
  padding: 40px;
  width: 100%;
  background-color: rgba(255, 255, 255, 0.70);
  margin-bottom: 30px;

}

.theme--dark .filterBox {
  background-color: rgba(27, 33, 36);

}

.resultBoxSidebar {

  width: 280px;
  padding: 25px;
  background-color: rgba(255, 255, 255, 0.70);
  box-shadow: 0px 7px 64px 0px rgba(0, 0, 0, 0.07);
}

.theme--dark .resultBoxSidebar {
  /* background-color: rgba(26, 27, 29,0.8); */
  background: linear-gradient(rgba(26, 27, 29), rgba(26, 27, 29, 0.25))


}

.resultBoxMain {
  border: 1px solid var(--v-primary-base);
  border-radius: 0px 16px 16px 0px;
  width: 100%;
  background: linear-gradient(90deg, #a0cce916 90%, transparent);
}

.resultTab {
  padding: 12px;
  cursor: pointer;
}

.resultTabActive {
  background-color: white;
  border-radius: 4px;
  border-left: 4px solid var(--v-primary-base);
  font-weight: 700;
}

.theme--dark .resultTabActive {
  background-color: #18191A;
}


/* 
.resultTabActive{
  #EBEAFA
} */

.predix-explore-panels .v-expansion-panel-header {
  border: none !important;
  border-radius: 6px !important;
  min-height: 4rem !important;
  height: 4rem !important;
  box-shadow: none !important;
}

.v-expansion-panel-header::before,
.v-expansion-panel-header:before {
  opacity: 0 !important;
}

.v-expansion-panel-content {
  border-width: 0px 1px 1px 1px !important;
  border-color: #0393A850 !important;
  border-style: solid !important;
}

.predix-explore-panels .v-expansion-panel--active > .v-expansion-panel-header {
  min-height: 4rem !important;
  height: 4rem !important;
  border-radius: 6px 6px 0 0 !important;
}

.predix-explore-panels :deep(.v-expansion-panel::before) {
  opacity: 0 !important;
  box-shadow: none !important;
}

:deep(.v-expansion-panel-content__wrap) {
  padding: 0 !important;
}

/* Panel shell only — never override height/overflow on .v-expansion-panel-content (breaks VExpandTransition). */
.predix-explore-panels :deep(.v-expansion-panel) {
  overflow: visible !important;
}

.predix-explore-panels :deep(.v-expansion-panel--active .v-expansion-panel-content__wrap) {
  overflow: visible;
}

.predix-explore-panels :deep(.v-expansion-panel-header__icon .v-icon) {
  transition: transform 280ms cubic-bezier(0.4, 0, 0.2, 1);
}

/* Window scroll + table row expand: avoid scroll-anchoring on the main column */
.predix-casting-main {
  overflow-anchor: none;
}

.predix-explore-panels :deep(.v-expansion-panel) {
  transition: box-shadow 280ms cubic-bezier(0.4, 0, 0.2, 1), margin-top 280ms cubic-bezier(0.4, 0, 0.2, 1), border-color 280ms cubic-bezier(0.4, 0, 0.2, 1), opacity 280ms cubic-bezier(0.4, 0, 0.2, 1);
}
.castingBox {
  width: 100%;
  height: 100%;
}

.emptyState {
  width: 250px;
  text-align: center;
}

.predix-dataset-pill-trigger {
  position: relative;
  display: inline-flex;
  padding: 0;
  margin: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  font: inherit;
  border-radius: 9999px;
  vertical-align: middle;
}

.predix-dataset-pill-trigger--current {
  box-shadow: 0 0 0 2px color-mix(in srgb, var(--v-primary-base) 55%, transparent);
  border-radius: 9999px;
}

.predix-dataset-pill-trigger:focus {
  outline: none;
}

.predix-dataset-pill-trigger:focus-visible {
  outline: 2px solid rgba(3, 147, 168, 0.55);
  outline-offset: 2px;
}

.predix-dataset-pill-trigger :deep(.predix-dataset-pill) {
  display: block;
  max-width: none;
  width: 100%;
  padding-inline: calc(var(--spacing) * 2);
  text-align: center;
}

/*
 * Composition sidebar: ~half of the previous ~1/3 column → fixed px so open/close
 * animate smoothly (browsers don’t interpolate % ↔ rem well; min-width fights collapse).
 */
.predix-composition-aside {
  --predix-composition-open-w: 390px;
  --predix-composition-collapsed-w: 64px;
  /* Horizontal + bottom inset so scroll content (e.g. loading card) never hugs the rail */
  padding: 0 18px 20px 18px;
  flex-grow: 0;
  flex-shrink: 0;
  min-width: 0;
  transition:
    width 240ms cubic-bezier(0.4, 0, 0.2, 1),
    flex-basis 240ms cubic-bezier(0.4, 0, 0.2, 1),
    max-width 240ms cubic-bezier(0.4, 0, 0.2, 1);
}

.predix-composition-aside--open {
  flex-basis: var(--predix-composition-open-w);
  width: var(--predix-composition-open-w);
  max-width: var(--predix-composition-open-w);
}

.predix-composition-aside--collapsed {
  flex-basis: var(--predix-composition-collapsed-w);
  width: var(--predix-composition-collapsed-w);
  max-width: var(--predix-composition-collapsed-w);
}

.predix-dataset-picker-list {
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.2) transparent;
}

/* Dataset modal list: selected row uses app primary (ModalPage “plan” pattern) */
.predix-dataset-modal-item--selected {
  border-color: var(--v-primary-base) !important;
}

.predix-dataset-modal-check {
  color: var(--v-primary-base);
}

.predix-dataset-refresh {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border-radius: 9999px;
  border: 1px solid rgba(209, 213, 219, 0.9);
  background-color: rgb(243, 244, 246);
  color: rgb(31, 41, 55);
  cursor: pointer;
  transition: background-color 150ms ease, border-color 150ms ease;
}

.predix-dataset-refresh:hover {
  background-color: rgb(229, 231, 235);
  border-color: rgb(209, 213, 219);
}

html.dark .predix-dataset-refresh {
  border-color: rgba(255, 255, 255, 0.1);
  background-color: rgba(255, 255, 255, 0.05);
  color: rgb(243, 244, 246);
}

html.dark .predix-dataset-refresh:hover {
  background-color: rgba(255, 255, 255, 0.1);
}
</style>
